import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Star, Camera, Upload, ThumbsUp, Filter, MessageSquare, Calendar, User, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ReviewsPage() {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('all');
  const [reviews, setReviews] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [completedBookings, setCompletedBookings] = useState([]);
  
  // New review form state
  const [newReview, setNewReview] = useState({
    booking_id: '',
    property_id: '',
    rating: 5,
    title: '',
    comment: '',
    photos: []
  });

  useEffect(() => {
    if (activeTab === 'all') {
      fetchAllReviews();
    } else if (activeTab === 'my' && user) {
      fetchMyReviews();
      fetchCompletedBookings();
    }
  }, [activeTab, sortBy, user]);

  const fetchAllReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reviews`, {
        params: { sort_by: sortBy, limit: 50 }
      });
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/reviews/user`);
      setMyReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Failed to fetch user reviews:', error);
      toast.error('Failed to load your reviews');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings/my-bookings`);
      const completed = response.data.filter(booking => 
        booking.status === 'completed' && 
        !myReviews.some(review => review.booking_id === booking.id)
      );
      setCompletedBookings(completed);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!newReview.booking_id || !newReview.property_id) {
      toast.error('Please select a booking to review');
      return;
    }

    try {
      await axios.post(`${API}/reviews`, newReview);
      
      toast.success('Review submitted successfully!');
      setShowWriteReview(false);
      setNewReview({
        booking_id: '',
        property_id: '',
        rating: 5,
        title: '',
        comment: '',
        photos: []
      });
      
      fetchMyReviews();
      
    } catch (error) {
      console.error('Failed to submit review:', error);
      const message = error.response?.data?.detail || 'Failed to submit review';
      toast.error(message);
    }
  };

  const markHelpful = async (reviewId, isHelpful) => {
    try {
      await axios.post(`${API}/reviews/${reviewId}/helpful?is_helpful=${isHelpful}`);
      
      // Update the review in the current list
      if (activeTab === 'all') {
        setReviews(prev => prev.map(review => 
          review.id === reviewId 
            ? { ...review, helpful_count: (review.helpful_count || 0) + (isHelpful ? 1 : -1) }
            : review
        ));
      }
      
      toast.success(isHelpful ? 'Marked as helpful' : 'Feedback recorded');
    } catch (error) {
      console.error('Failed to mark helpful:', error);
      toast.error('Failed to record feedback');
    }
  };

  const StarRating = ({ rating, editable = false, onRatingChange = null }) => {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={20}
            style={{
              cursor: editable ? 'pointer' : 'default',
              color: star <= rating ? '#fbbf24' : '#d1d5db',
              fill: star <= rating ? '#fbbf24' : 'none'
            }}
            onClick={() => editable && onRatingChange && onRatingChange(star)}
          />
        ))}
      </div>
    );
  };

  const ReviewCard = ({ review, showProperty = true }) => {
    return (
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        border: '1px solid #e2e8f0',
        marginBottom: '20px'
      }}>
        {/* Review Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '600'
            }}>
              {review.traveler_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#2d3748' }}>
                {review.traveler_name}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Calendar size={14} />
                {new Date(review.created_at).toLocaleDateString()}
                {review.is_verified && (
                  <span style={{ color: '#10b981', fontWeight: '500', marginLeft: '8px' }}>
                    ✓ Verified Stay
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <StarRating rating={review.rating} />
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>
              {review.rating}/5 stars
            </div>
          </div>
        </div>

        {/* Property Info (if showing) */}
        {showProperty && review.property_info && (
          <div style={{
            background: '#f8fafc',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            {review.property_info.main_photo && (
              <img
                src={review.property_info.main_photo}
                alt="Property"
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            )}
            <div>
              <div style={{ fontWeight: '600', color: '#2d3748' }}>
                {review.property_info.name}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MapPin size={14} />
                {review.property_info.location}
              </div>
            </div>
          </div>
        )}

        {/* Review Content */}
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
            {review.title}
          </h4>
          <p style={{ color: '#4a5568', lineHeight: '1.6' }}>
            {review.comment}
          </p>
        </div>

        {/* Review Photos */}
        {review.photos && review.photos.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px' }}>
              {review.photos.slice(0, 4).map((photo, index) => (
                <div key={index} style={{ position: 'relative' }}>
                  <img
                    src={photo}
                    alt={`Review photo ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '8px'
                    }}
                  />
                  {index === 3 && review.photos.length > 4 && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '0.875rem',
                      fontWeight: '600'
                    }}>
                      +{review.photos.length - 4}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Review Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
          <button
            onClick={() => markHelpful(review.id, true)}
            style={{
              background: 'none',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '0.875rem'
            }}
          >
            <ThumbsUp size={16} />
            Helpful ({review.helpful_count || 0})
          </button>
          
          <div style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
            {review.is_verified ? 'Verified Review' : 'Unverified'}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 0', minHeight: '60vh' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '20px', color: '#718096' }}>Loading reviews...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)', background: '#f7fafc' }}>
      <div className="container">
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '2.25rem', 
            fontWeight: '700', 
            marginBottom: '16px',
            color: '#2d3748'
          }}>
            Guest Reviews
          </h1>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>
            Share your ZypeeTravel experiences and read what other guests have to say
          </p>
        </div>

        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '24px', 
          marginBottom: '32px',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: '16px'
        }}>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '8px 0',
              border: 'none',
              background: 'none',
              fontSize: '1rem',
              fontWeight: '500',
              color: activeTab === 'all' ? '#10b981' : '#6b7280',
              borderBottom: activeTab === 'all' ? '2px solid #10b981' : 'none',
              cursor: 'pointer'
            }}
          >
            All Reviews
          </button>
          
          {user && (
            <button
              onClick={() => setActiveTab('my')}
              style={{
                padding: '8px 0',
                border: 'none',
                background: 'none',
                fontSize: '1rem',
                fontWeight: '500',
                color: activeTab === 'my' ? '#10b981' : '#6b7280',
                borderBottom: activeTab === 'my' ? '2px solid #10b981' : 'none',
                cursor: 'pointer'
              }}
            >
              My Reviews
            </button>
          )}
        </div>

        {/* Filters and Actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          {activeTab === 'all' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Filter size={20} color="#6b7280" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  fontSize: '0.875rem'
                }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="rating_high">Highest Rating</option>
                <option value="rating_low">Lowest Rating</option>
                <option value="helpful">Most Helpful</option>
              </select>
            </div>
          )}

          {activeTab === 'my' && user && completedBookings.length > 0 && (
            <button
              onClick={() => setShowWriteReview(true)}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <MessageSquare size={20} />
              Write a Review
            </button>
          )}
        </div>

        {/* Review Content */}
        {activeTab === 'all' && (
          <div>
            {reviews.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px', 
                background: 'white', 
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <MessageSquare size={48} style={{ margin: '0 auto 16px', color: '#a0aec0' }} />
                <p style={{ color: '#718096', fontSize: '1.1rem' }}>No reviews yet</p>
                <p style={{ color: '#a0aec0', fontSize: '0.9rem' }}>Be the first to share your experience!</p>
              </div>
            ) : (
              reviews.map(review => (
                <ReviewCard key={review.id} review={review} showProperty={true} />
              ))
            )}
          </div>
        )}

        {activeTab === 'my' && user && (
          <div>
            {!showWriteReview && myReviews.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px', 
                background: 'white', 
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <MessageSquare size={48} style={{ margin: '0 auto 16px', color: '#a0aec0' }} />
                <p style={{ color: '#718096', fontSize: '1.1rem' }}>You haven't written any reviews yet</p>
                {completedBookings.length > 0 && (
                  <button
                    onClick={() => setShowWriteReview(true)}
                    className="btn btn-primary"
                    style={{ marginTop: '16px' }}
                  >
                    Write Your First Review
                  </button>
                )}
              </div>
            )}

            {myReviews.map(review => (
              <ReviewCard key={review.id} review={review} showProperty={false} />
            ))}
          </div>
        )}

        {/* Write Review Modal */}
        {showWriteReview && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto'
            }}>
              <h3 style={{ 
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '24px',
                color: '#2d3748'
              }}>
                Write a Review
              </h3>

              <form onSubmit={handleSubmitReview}>
                {/* Booking Selection */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                    Select Booking to Review
                  </label>
                  <select
                    value={newReview.booking_id}
                    onChange={(e) => {
                      const booking = completedBookings.find(b => b.id === e.target.value);
                      setNewReview({
                        ...newReview,
                        booking_id: e.target.value,
                        property_id: booking ? booking.property_id : ''
                      });
                    }}
                    required
                    className="form-input"
                    style={{ width: '100%' }}
                  >
                    <option value="">Choose a booking...</option>
                    {completedBookings.map(booking => (
                      <option key={booking.id} value={booking.id}>
                        Booking #{booking.id.slice(-8)} - {new Date(booking.check_in_date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Rating */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                    Rating
                  </label>
                  <StarRating 
                    rating={newReview.rating} 
                    editable={true} 
                    onRatingChange={(rating) => setNewReview({...newReview, rating})}
                  />
                </div>

                {/* Title */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                    Review Title
                  </label>
                  <input
                    type="text"
                    value={newReview.title}
                    onChange={(e) => setNewReview({...newReview, title: e.target.value})}
                    placeholder="Summarize your experience..."
                    required
                    className="form-input"
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Comment */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                    Your Review
                  </label>
                  <textarea
                    value={newReview.comment}
                    onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                    placeholder="Share your experience with other travelers..."
                    required
                    rows="4"
                    className="form-input"
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>

                {/* Photo Upload Placeholder */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4a5568' }}>
                    Photos (Optional)
                  </label>
                  <div style={{
                    border: '2px dashed #d1d5db',
                    borderRadius: '8px',
                    padding: '32px',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <Camera size={32} style={{ margin: '0 auto 8px', color: '#9ca3af' }} />
                    <p style={{ margin: 0, fontSize: '0.875rem' }}>
                      Photo upload feature coming soon!
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'end' }}>
                  <button
                    type="button"
                    onClick={() => setShowWriteReview(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Submit Review
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReviewsPage;